/**
 * findingFixResolver — ONE primary fix action per audit finding.
 *
 * Before this module, only 2 of 22 check families had a structured fix
 * (the Reconcile value-picker on R-67/tier-2e, later R-79); every other
 * finding fell through to a link that merely opened the report. The
 * resolver derives, per finding, the single best remediation the platform
 * can actually execute — using ONLY mechanisms that already exist:
 *
 *  - `reconcile`   → value-picker → `recordAuditOverride` (recon:{id})
 *  - `amend-sed`   → ResolveAmendmentDialog → `saveSEDAmendment`
 *                    (R-78: the precedence violation already carries the SED
 *                    dot-path in `provenance` and the corrected value in
 *                    `tier1Value` — no new detection, pure projection)
 *  - `edit-intent` → Project Intent surface (`updateSiteProjectIntent`);
 *                    intent is NEVER amended through the SED path
 *  - `apply-directive` → the report's sanity-finding review flow
 *                    (SanityCheckCard apply/ignore — the healthiest
 *                    existing fix loop)
 *  - `regenerate`  → `generateTypedReport('update', parentId)` in place
 *  - `open-report` / `open-link` → navigation-only fallbacks
 *  - `view-detail` → expandable payload only
 *
 * Pure module: no React, no store, no I/O — fully unit-testable. Findings
 * stay FACTS; the action is derived at render time, never persisted.
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { UserAuditFinding } from '../../types/userAudit';
import { isUnavailableFloodPrecedence } from './floodZoneAvailability';

export type FindingFix =
  | { kind: 'reconcile'; label: string; candidateValues: string[] }
  | {
      kind: 'amend-sed';
      label: string;
      /** Dotted SED path (the `sed.` prefix already stripped). */
      variablePath: string;
      /** Human label for the amended value (the canonical fact name). */
      variableLabel: string;
      /** Evidence domain — first path segment ("grid", "climate", …). */
      domain: string;
      oldValue: string | number;
      suggestedValue: string | number;
    }
  | { kind: 'edit-intent'; label: string; href: string }
  | { kind: 'apply-directive'; label: string; reportId: string }
  | { kind: 'regenerate'; label: string; reportId: string }
  | { kind: 'open-link'; label: string; href: string }
  | { kind: 'view-detail'; label: string }
  | { kind: 'none'; label: string };

export interface FindingFixContext {
  siteId: string;
  siteReports: SiteRagReport[];
  /** Present when the site has a SED the amend path can target. */
  sedId?: string;
  /** Whether the finding has an expandable payload (detail/context/…). */
  hasPayload: boolean;
}

/** Internal-number / cross-report divergence checks that offer Reconcile. */
export function isReconcilableFinding(f: UserAuditFinding): boolean {
  if (f.checkId === 'R-67' || f.checkId === 'tier-2e') return true;
  // R-79 / tier-3 cross-report divergence carries a public canonical fact key
  // plus structured candidates — the recorded override settles the divergence
  // (settledFactKeys / reconcileOverrides) and applies at regeneration. tier-3
  // findings gained structured candidates 2026-08-12; before that their only
  // affordances were Validate/Dismiss, leaving no way to pick the right value.
  return (
    (f.checkId === 'R-79' || f.checkId === 'tier-3')
    && Boolean(f.canonicalFactKey)
    && (f.reconcileCandidates?.length ?? 0) >= 2
  );
}

/**
 * F — candidate sanity filter. Extraction slips (a regex capturing "is 2" out
 * of "the nearest substation is 2.9 km", or a project descriptor) must degrade
 * to FEWER options, never nonsense options. Drops candidates that lead with a
 * verb/stopword or reduce to a bare numeric fragment when the fact is
 * text-valued alongside real names.
 */
const CANDIDATE_STOPWORD_RE = /^(is|was|are|were|be|being|been|lies|sits|located|approximately|about|roughly|the|a|an)\b/i;

export function sanitizeReconcileCandidates(candidates: string[]): string[] {
  const filtered = candidates.filter((c) => {
    const t = c.trim();
    if (t.length === 0) return false;
    if (CANDIDATE_STOPWORD_RE.test(t)) return false;
    return true;
  });
  // Never filter down below a usable picker if everything was dropped —
  // return the survivors even if < 2; callers already gate on length >= 2.
  return filtered;
}

/**
 * Parse the distinct values out of an internal-number inconsistency finding,
 * e.g. "Capacity (MW) cited with 3 distinct values in same report: 1500, 1750, 750".
 * Falls back to the per-mention context block ("  - 1500 → \"…\"").
 *
 * (Moved verbatim from SiteAuditTab — the SSOT-ordering + numeric-cleaning
 * variant is the canonical finding-level parser; `runUserAudit`'s emit-time
 * `parseCandidateValues` remains the message-level detector.)
 */
export function parseReconcileCandidates(f: UserAuditFinding): string[] {
  // Structured candidates (R-79) — typed values from the reconciliation,
  // source-of-truth first. Never fall through to prose parsing for these.
  if ((f.reconcileCandidates?.length ?? 0) >= 2) return f.reconcileCandidates!;
  // The message can carry TWO value lists:
  //   "…cited with 3 unreconciled value(s) in same report: 500, 300, 50
  //    (none match the phase SSOT: 550, 350, 900)"
  // Offer the SSOT list first (it is the declared source of truth), then the
  // cited values. `clean` extracts the LEADING numeric token so prose tails
  // ("50. Source of truth…", "1170.1 acres", "900)") never leak into options.
  const clean = (raw: string): string => {
    const m = raw.trim().match(/^[-+]?\d[\d,]*(?:\.\d+)?/);
    return m ? m[0].replace(/,+$/, '') : '';
  };
  const lists: string[][] = [];
  for (const segment of f.message.split(':').slice(1)) {
    const values = segment
      .split(',')
      .map(clean)
      .filter((v) => /^[-+]?\d[\d,.]*$/.test(v));
    if (values.length > 0) lists.push(values);
  }
  if (lists.length > 0) {
    // SSOT parenthetical comes last in the message — surface its values first.
    const ordered = (lists.length > 1 ? [...lists].reverse() : lists).flat();
    const deduped = ordered.filter((v, i) => ordered.indexOf(v) === i);
    if (deduped.length >= 2) return deduped;
  }
  if (f.context) {
    const seen: string[] = [];
    for (const line of f.context.split('\n')) {
      const m = line.match(/^\s*-\s*([^→\n]+?)\s*(?:→|$)/);
      if (m && m[1] && !seen.includes(m[1].trim())) seen.push(m[1].trim());
    }
    if (seen.length >= 2) return seen;
  }
  return [];
}

/**
 * Resolve the target report for report-scoped actions: the finding's own
 * report, else the freshest report of the finding's type.
 */
export function resolveTargetReport(
  f: UserAuditFinding,
  siteReports: SiteRagReport[],
): SiteRagReport | undefined {
  return (
    (f.reportId ? siteReports.find((r) => r.id === f.reportId) : undefined) ??
    (f.reportType
      ? [...siteReports]
          .filter((r) => (r.reportType ?? 'feasibility') === f.reportType)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      : undefined)
  );
}

/** Check families whose fix is regenerating the affected report in place. */
const REGENERATE_CHECKS: ReadonlySet<UserAuditFinding['checkId']> = new Set([
  'tier-1',
  'tier-2b',
  'tier-2d',
  'tier-2f',
  'tier-2k',
  'tier-2l',
  'tier-2m',
  'tier-2n',
  'tier-3',
  'tier-4',
  'R-3',
  'R-73',
]);

/** Check families resolved through the report's sanity/quality review flow. */
const DIRECTIVE_CHECKS: ReadonlySet<UserAuditFinding['checkId']> = new Set([
  'R-8',
  'R-9',
  'R-15',
]);

export function resolveFindingFix(f: UserAuditFinding, ctx: FindingFixContext): FindingFix {
  // (1) Value conflicts → Reconcile value-picker (existing flow).
  if (isReconcilableFinding(f)) {
    const candidateValues = sanitizeReconcileCandidates(parseReconcileCandidates(f));
    if (candidateValues.length >= 2) {
      return { kind: 'reconcile', label: 'Fix value…', candidateValues };
    }
  }

  // (2) R-78 precedence violation → the REPORT contradicts the tier-1 value
  // (which the SED already holds at `provenance`), so the primary fix is
  // regenerating the report re-grounded on tier-1. Disputing the tier-1
  // value itself is the SECONDARY fix — see `resolveSecondaryFix`.
  if (f.checkId === 'R-78') {
    if (isUnavailableFloodPrecedence(f.precedenceViolation)) {
      return { kind: 'open-link', label: 'Review cited flood source', href: `/sites/${encodeURIComponent(ctx.siteId)}?tab=evidence` };
    }
    const target = resolveTargetReport(f, ctx.siteReports);
    if (target) {
      return { kind: 'regenerate', label: 'Regenerate with verified value', reportId: target.id };
    }
  }

  // (3) R-65 intent/SED divergence → the fix lives on the Intent surface
  // (never through the SED path). The host adds the one-click "Use SED
  // value" when the reconciliation carries a typed sedSyncValue.
  if (f.checkId === 'R-65') {
    return {
      kind: 'edit-intent',
      label: 'Edit intent',
      href: f.actionHref ?? `/sites/${encodeURIComponent(ctx.siteId)}?tab=intent`,
    };
  }

  // (4) Sanity/quality findings → the report's apply/ignore review flow.
  if (DIRECTIVE_CHECKS.has(f.checkId)) {
    const target = resolveTargetReport(f, ctx.siteReports);
    if (target) return { kind: 'apply-directive', label: 'Review & apply fix', reportId: target.id };
  }

  // Missing source facts cannot be supplied by regeneration — route to the
  // evidence/documents surface instead of claiming "Fixed on regeneration".
  const missingSourceFacts =
    (Boolean(f.actionHref) && /[?&]tab=(?:documents|evidence)\b/i.test(f.actionHref!))
    || /\b(?:explicitly omitted|evidence sources returned no facts|add sources|do not regenerate|section is unevidenced|missing source|no source facts)\b/i.test(
      `${f.message ?? ''} ${f.detail ?? ''} ${f.action ?? ''}`,
    );
  if (missingSourceFacts) {
    return {
      kind: 'open-link',
      label: f.action && !/regenerate|expand section/i.test(f.action)
        ? f.action
        : 'Add sources — section is unevidenced',
      href: f.actionHref && /[?&]tab=(?:documents|evidence)\b/i.test(f.actionHref)
        ? f.actionHref
        : `/sites/${encodeURIComponent(ctx.siteId)}?tab=documents`,
    };
  }

  // (5) Content checks → regenerate the affected report in place.
  if (REGENERATE_CHECKS.has(f.checkId)) {
    const target = resolveTargetReport(f, ctx.siteReports);
    if (target) return { kind: 'regenerate', label: 'Regenerate report', reportId: target.id };
  }

  // (6) Navigation fallback (source doc, financial tab, report editor…).
  if (f.actionHref) {
    return { kind: 'open-link', label: f.action ?? 'Open', href: f.actionHref };
  }

  if (ctx.hasPayload) return { kind: 'view-detail', label: 'View detail' };
  return { kind: 'none', label: f.action ?? '' };
}

/**
 * Secondary fix, rendered as a smaller affordance next to the primary.
 *
 * R-78 only (today): DISPUTE the tier-1 value — the user believes the
 * verified provider value is wrong for this site. Routes through
 * ResolveAmendmentDialog → `saveSEDAmendment`, the override-with-rationale
 * escape hatch: the SED slot at `provenance` currently holds `tier1Value`;
 * the report's own value is offered as the plausible alternative. Fixing it
 * at the SOURCE means every future report inherits the correction.
 */
export function resolveSecondaryFix(f: UserAuditFinding, ctx: FindingFixContext): FindingFix | null {
  if (isUnavailableFloodPrecedence(f.precedenceViolation)) return null;
  if (f.checkId !== 'R-78' || !ctx.sedId || !f.precedenceViolation?.provenance) return null;
  const variablePath = f.precedenceViolation.provenance.replace(/^sed\./, '');
  // `applySingleAmendmentToSed` requires a deep-enough dotted path; a bare
  // segment would fail the apply, so don't offer the action for one.
  if (!variablePath.includes('.')) return null;
  return {
    kind: 'amend-sed',
    label: 'Dispute verified value…',
    variablePath,
    variableLabel: f.precedenceViolation.fact,
    domain: variablePath.split('.')[0] ?? 'evidence',
    // The SED currently holds the tier-1 value; the report's value is the
    // pre-filled alternative the user is likely asserting instead.
    oldValue: f.precedenceViolation.tier1Value,
    suggestedValue: f.precedenceViolation.reportedValue,
  };
}
